import React, { FC } from "react";
import { DateTime } from "luxon";
import "./SplitPaymentInfo.scss";

export type SplitPaymentStatusKind = "paid" | "partiallyPaid" | "pending" | null;

export function getBookingSplitData(booking: any): any[] | null {
  const splitData = booking?.itineraryCart?.splitData;
  if (!Array.isArray(splitData) || splitData.length === 0) return null;
  return splitData;
}

export function getSplitPaymentStatusKind(booking: any): SplitPaymentStatusKind {
  const splitData = getBookingSplitData(booking);
  if (!splitData) return null;
  const statuses = splitData.map((row) =>
    String(row?.status ?? "Pending").toLowerCase()
  );
  const successCount = statuses.filter((s) => s === "success").length;
  if (successCount === splitData.length) return "paid";
  if (successCount > 0) return "partiallyPaid";
  return "pending";
}

export function getBookingDisplayCurrency(booking: any): string {
  return (
    booking?.currency ??
    booking?.itineraryCart?.currency ??
    booking?.itineraryCart?.searchparams?.currency ??
    "CAD"
  );
}

function formatSplitCurrency(amount: number, currency: string) {
  const value = Number(amount ?? 0);
  return `${currency} $${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDueDate(dueDate: string | number | undefined) {
  if (dueDate == null || dueDate === "") return "";
  const parsed =
    typeof dueDate === "number"
      ? DateTime.fromMillis(dueDate)
      : DateTime.fromISO(String(dueDate));
  if (!parsed.isValid) {
    const asDate = new Date(dueDate as string | number);
    if (Number.isNaN(asDate.getTime())) return "";
    return DateTime.fromJSDate(asDate).toFormat("MMM d, yyyy");
  }
  return parsed.toFormat("MMM d, yyyy");
}

function statusAccentColor(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === "success") return "#4CAF50";
  if (normalized === "pending") return "#FFC107";
  return "#E53935";
}

interface SplitPaymentInfoProps {
  booking: any;
  showHeading?: boolean;
}

const SplitPaymentInfo: FC<SplitPaymentInfoProps> = ({
  booking,
  showHeading = true,
}) => {
  const splitData = getBookingSplitData(booking);
  if (!splitData?.length) return null;

  const currency = getBookingDisplayCurrency(booking);

  return (
    <div className="splitPaymentInfo">
      {showHeading && (
        <h6 className="splitPaymentInfo__title">Split Information</h6>
      )}
      {splitData.map((split: any, idx: number) => {
        const statusRaw = String(split?.status ?? "Pending");
        const status = statusRaw.toLowerCase();
        const borderColor = statusAccentColor(status);

        return (
          <div
            key={split?.id ?? idx}
            className="splitPaymentInfo__card"
            style={{ borderLeftColor: borderColor }}
          >
            <div className="splitPaymentInfo__cardMain">
              <strong className="splitPaymentInfo__name">
                {split?.name || "User"}
              </strong>
              {split?.email && (
                <div className="splitPaymentInfo__email">{split.email}</div>
              )}
              {split?.dueDate && (
                <span className="splitPaymentInfo__due badge bg-warning text-dark">
                  Due: {formatDueDate(split.dueDate)}
                </span>
              )}
            </div>
            <div className="splitPaymentInfo__cardAmount">
              <div className="splitPaymentInfo__amount">
                {formatSplitCurrency(Number(split?.amount ?? 0), currency)}
              </div>
              <div
                className="splitPaymentInfo__status"
                style={{ color: borderColor }}
              >
                {statusRaw}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SplitPaymentInfo;
